import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useCookieConsent } from '../contexts/CookieConsentContext'
import api from '../utils/api'
import logger from '../utils/logger'
import { isIntegrationsAdmin } from '../utils/integrationsAccess'
import HeroPill from './HeroPill'
import CallBookingModal from './CallBookingModal'
import PromoBanner from './PromoBanner'
import TrialCountdownBanner from './TrialCountdownBanner'
import ViewOnlyLockHero from './ViewOnlyLockHero'
import usePromoBanner from '../hooks/usePromoBanner'
import useActivation from '../hooks/useActivation'
import {
  ChartBarIcon,
  FolderIcon,
  DocumentTextIcon,
  EyeIcon,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  CpuChipIcon,
  SparklesIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  PuzzlePieceIcon,
  GiftIcon,
  UserGroupIcon,
  HomeIcon,
  ChartPieIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  PlayIcon,
  PencilSquareIcon,
  VideoCameraIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline'

// Новая структура навигации с секциями
const navigationSections = [
  {
    title: null,
    items: [
      {
        name: 'Обучение',
        href: '/welcome',
        icon: SparklesIcon,
        description: 'Первые шаги и скидка на Starter',
        tourId: 'welcome',
        // Shown only while the user is on the activation journey
        hiddenIf: (user) => !user || !!user.activationCompletedAt || !!user.activationDismissedAt,
        dynamicBadge: 'activation',
      },
      {
        name: 'Главная',
        href: '/training',
        icon: HomeIcon,
        description: 'Обучающие материалы и AI Agent для работы с платформой',
        tourId: 'training'
      }
    ]
  },
  {
    title: 'АНАЛИЗ',
    items: [
      {
        name: 'Дашборд',
        href: '/dashboard',
        icon: ChartBarIcon,
        description: 'Аналитика и собранный контент',
        tourId: 'dashboard'
      },
      {
        name: 'Лента',
        href: '/lenta',
        icon: RectangleStackIcon,
        description: 'Лента трендовых видео по категориям',
        badge: 'new',
        tourId: 'lenta'
      },
      {
        name: 'Аккаунты',
        href: '/collections',
        icon: FolderIcon,
        description: 'Управление группами блогеров',
        tourId: 'collections'
      },
      {
        name: 'Мониторинг',
        href: '/monitoring',
        icon: EyeIcon,
        description: 'Мониторинг активности аккаунтов',
        emailOnly: ['grandchef@retaste.ru']
      },
    ]
  },
  {
    title: 'ИИ',
    items: [
      {
        name: 'Чат',
        href: '/ai-chat',
        icon: ChatBubbleLeftRightIcon,
        description: 'AI-ассистент для анализа контента',
        tourId: 'ai-chat'
      },
      {
        name: 'Анализ профиля',
        href: '/video-analysis?tab=profile',
        icon: ChartPieIcon,
        description: 'Глубокий AI-анализ профиля',
        tourId: 'profile-analysis',
      },
      {
        name: 'Анализ видео',
        href: '/video-analysis',
        icon: VideoCameraIcon,
        description: 'AI разбор видео — хуки, структура, тренды',
        tourId: 'video-analysis',
      },
      {
        name: 'Поиск идей',
        href: '/ideas-lab',
        icon: MagnifyingGlassIcon,
        description: 'Поиск вирусных идей для контента',
        badge: 'new',
        tourId: 'ideas-lab'
      },
      {
        name: 'Воркспейс',
        href: '/ai',
        icon: CpuChipIcon,
        description: 'Визуальное рабочее пространство с AI',
        tourId: 'ai'
      },
    ]
  },
  {
    title: 'СОЗДАНИЕ',
    items: [
      {
        name: 'Редактор',
        href: '/video-analysis?tab=editor',
        icon: PencilSquareIcon,
        description: 'Сценарии, тексты и посты с AI-помощником',
        tourId: 'editor',
      },
    ]
  },
]

const adminNavigation = [
  { name: 'Админ', href: '/admin', icon: ShieldCheckIcon },
]

// Интеграции (MCP/CLI/Skills) — на MVP-этапе доступно только integrations
// admin email whitelist (см. utils/integrationsAccess.js). Скрыто для всех
// остальных, потому что каждый MCP-вызов = реальные деньги через
// SC/Apify/Gemini, и пока не закрыт биллинг — не открываем для всех.
const integrationsNavigation = [
  { name: 'Интеграции', href: '/integrations', icon: PuzzlePieceIcon },
]

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [subscription, setSubscription] = useState(null)
  const [showCallBooking, setShowCallBooking] = useState(false)
  const { user, logout, completeOnboarding } = useAuthStore()
  const { reopenBanner } = useCookieConsent()
  const location = useLocation()
  const navigate = useNavigate()

  // Redirect new users to onboarding flow
  useEffect(() => {
    if (user && !user.onboardingCompletedAt) {
      navigate('/onboarding/step-1')
    }
  }, [user, navigate])

  // Show call booking modal 1-14 days after registration (if onboarding completed).
  // Dismiss/submit state is stored on the User record, not localStorage — so
  // closing the modal once hides it across all devices.
  useEffect(() => {
    if (!user || !user.onboardingCompletedAt) return
    if (user.callBooking?.dismissedAt || user.callBooking?.submittedAt) return

    const daysSinceCreation = (Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24)
    if (daysSinceCreation >= 1 && daysSinceCreation <= 14) {
      setShowCallBooking(true)
    }
  }, [user])

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/api/subscriptions/my')
        setSubscription(response.data.data)
      } catch (error) {
        logger.error('Failed to fetch subscription:', error)
      }
    }

    if (user) {
      fetchSubscription()
    }
  }, [user])

  const handleLogout = () => {
    logout()
  }

  const formatSubscriptionEnd = (date) => {
    if (!date) return null
    const endDate = new Date(date)
    const today = new Date()
    const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))

    if (daysLeft < 0) return 'Истекла'
    if (daysLeft === 0) return 'Истекает сегодня'
    if (daysLeft === 1) return 'Истекает завтра'
    if (daysLeft <= 7) return `Осталось ${daysLeft} дн.`

    return endDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })
  }

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Trial countdown banner — sticky top, self-hides when not in trial */}
      <TrialCountdownBanner />

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 z-50 w-72 flex flex-col overflow-hidden bg-zinc-800">
            <div className="flex items-center justify-between p-6">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-brand-lime rounded-xl flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-brand-black" />
                </div>
                <h1 className="text-xl font-bold text-white">
                  Контент Борд
                </h1>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <SidebarContent currentPath={location.pathname} />
            </div>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow overflow-hidden bg-zinc-800">
          <div className="flex items-center px-6 py-5">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-brand-lime rounded-xl flex items-center justify-center">
                <SparklesIcon className="w-6 h-6 text-brand-black" />
              </div>
              <h1 className="text-xl font-bold text-white">
                Контент Борд
              </h1>
            </div>
          </div>
          <div className="flex-1 overflow-hidden">
            <SidebarContent currentPath={location.pathname} />
          </div>
        </div>
      </div>

      {/* Content wrapper */}
      <div className="lg:pl-72 bg-zinc-800 min-h-screen">
        {/* Mobile menu button */}
        <div className="lg:hidden sticky top-0 z-30 bg-zinc-800 px-4 py-2">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl hover:bg-white/10 transition-all duration-200"
          >
            <Bars3Icon className="w-6 h-6 text-slate-300" />
          </button>
        </div>

        {/* Wrap-around effect: zinc-800 padding acts as the visual border (desktop only) */}
        <div className="pt-0 px-0 lg:pt-5 lg:pr-5">
          <main className={`bg-slate-50 rounded-tl-2xl rounded-tr-2xl lg:rounded-tl-[32px] lg:rounded-tr-[32px] ${['/ai-chat', '/video-analysis'].includes(location.pathname) ? 'min-h-screen lg:h-[calc(100vh-1.25rem)] lg:overflow-hidden' : 'min-h-screen'}`}>
          <div className={['/ai-chat', '/video-analysis'].includes(location.pathname) ? 'p-4 sm:p-6 min-h-screen lg:h-full lg:overflow-hidden' : 'p-4 sm:p-6'}>
            {/* View-only lock hero — self-hides when lockState !== 'view_only' */}
            <ViewOnlyLockHero />
            {children}
          </div>

          {/* Footer inside main so it stays on white bg — hidden on fullscreen pages */}
          <footer className={`border-t border-slate-200 bg-slate-50 py-4 px-4 sm:px-6 ${['/ai-chat', '/video-analysis'].includes(location.pathname) ? 'hidden' : ''}`}>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-slate-500">
            <Link to="/offer" className="hover:text-slate-700 transition-colors">
              Оферта
            </Link>
            <Link to="/terms" className="hover:text-slate-700 transition-colors">
              Условия использования
            </Link>
            <Link to="/privacy" className="hover:text-slate-700 transition-colors">
              Политика конфиденциальности
            </Link>
            <Link to="/cookies" className="hover:text-slate-700 transition-colors">
              Политика cookie
            </Link>
            <button
              onClick={reopenBanner}
              className="hover:text-slate-700 transition-colors underline underline-offset-2"
            >
              Настройки cookie
            </button>
          </div>
          <p className="text-center text-[10px] text-slate-400 mt-2">
            © {new Date().getFullYear()} Контент Борд. ИП Скрипченков М.А.
          </p>
        </footer>
        </main>
        </div>
      </div>

      {/* Call Booking Modal (после онбординга) */}
      <CallBookingModal
        isOpen={showCallBooking}
        onClose={() => {
          setShowCallBooking(false)
          api.post('/api/users/call-booking/dismiss').catch(err => logger.error('Failed to persist call-booking dismiss:', err))
        }}
      />
    </div>
  )
}

const ProfileDropdown = ({ user, subscription }) => {
  const [open, setOpen] = useState(false)
  const { logout } = useAuthStore()
  const navigate = useNavigate()

  const initials = (user?.name || user?.email || '?').slice(0, 2).toUpperCase()
  const planName = subscription?.plan === 'free' ? 'Free' : subscription?.plan || ''

  const menuItems = [
    { label: 'Подписка', href: '/subscription', icon: CreditCardIcon, badge: planName },
    { label: 'Реферальная программа', href: '/referral', icon: GiftIcon },
    { label: 'Настройки', href: '/settings', icon: CogIcon },
  ]

  return (
    <div className="flex-shrink-0 px-3 pb-3 border-t border-white/10 pt-3 relative">
      {/* Trigger */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-all"
      >
        <div className="w-8 h-8 rounded-full bg-brand-lime flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-zinc-900">{initials}</span>
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-medium text-white truncate">{user?.name || user?.email}</p>
          {subscription && (
            <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
          )}
        </div>
        <svg className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
        </svg>
      </button>

      {/* Dropdown — opens upward */}
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-3 right-3 bottom-[calc(100%+4px)] z-50 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl shadow-black/40 overflow-hidden">

            {/* Limits */}
            {subscription && (
              <div className="px-4 pt-4 pb-3 border-b border-zinc-800">
                <div className="flex items-center justify-between mb-2.5">
                  <span className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">Лимиты</span>
                  <span className="text-[10px] font-bold text-brand-lime capitalize">{planName}</span>
                </div>
                <div className="space-y-2">
                  <div>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-zinc-500">Видео</span>
                      <span className="font-medium text-zinc-300 tabular-nums">{subscription.videoUsed} / {subscription.videoLimit}</span>
                    </div>
                    <div className="w-full bg-zinc-800 rounded-full h-1.5">
                      <div
                        className="bg-brand-lime h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min((subscription.videoUsed / subscription.videoLimit) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-[11px] mb-1">
                      <span className="text-zinc-500">AI запросы</span>
                      <span className="font-medium text-zinc-300 tabular-nums">
                        {subscription.aiUsed} / {subscription.plan === 'business' ? '\u221E' : subscription.aiLimit}
                      </span>
                    </div>
                    {subscription.plan !== 'business' && (
                      <div className="w-full bg-zinc-800 rounded-full h-1.5">
                        <div
                          className="bg-brand-lime h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${Math.min((subscription.aiUsed / subscription.aiLimit) * 100, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Menu items */}
            <div className="py-1.5">
              {menuItems.map(item => (
                <button
                  key={item.label}
                  onClick={() => { setOpen(false); navigate(item.href) }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-800/60 transition-colors"
                >
                  <item.icon className="w-4 h-4 text-zinc-500" />
                  <span className="flex-1 text-sm text-zinc-300">{item.label}</span>
                  {item.badge && (
                    <span className="text-[10px] font-bold text-brand-lime bg-brand-lime/10 px-1.5 py-0.5 rounded capitalize">{item.badge}</span>
                  )}
                </button>
              ))}
            </div>

            {/* Logout */}
            <div className="border-t border-zinc-800 p-2">
              <button
                onClick={() => { setOpen(false); logout() }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 transition-colors"
              >
                <ArrowLeftOnRectangleIcon className="w-4 h-4 text-red-400" />
                <span className="text-sm font-medium text-red-400">Выйти</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

const SidebarContent = ({ currentPath }) => {
  const { user } = useAuthStore()
  const location = useLocation()
  const isAdmin = user?.role === 'admin'
  const [subscription, setSubscription] = useState(null)

  // Activation state drives the /welcome nav item visibility + N/4 badge
  const showActivationNav = !!user && !!user.onboardingCompletedAt && !user.activationCompletedAt && !user.activationDismissedAt
  const { data: activationData } = useActivation({ pollMs: showActivationNav ? 30000 : 0 })
  const activationBadge = activationData ? `${activationData.progress}/${activationData.total}` : null

  // Promo banner (welcome for new users, comeback for churned) —
  // positioned at the bottom of the sidebar, above the profile dropdown
  const { banner: promoBanner, handleCta: handlePromoCta, handleDismiss: handlePromoDismiss } = usePromoBanner()

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await api.get('/api/subscriptions/my')
        setSubscription(response.data.data)
      } catch (error) {
        logger.error('Failed to fetch subscription:', error)
      }
    }

    if (user) {
      fetchSubscription()
    }
  }, [user])

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigationSections.map((section, sectionIdx) => (
          <div key={section.title} className={sectionIdx > 0 ? 'mt-8' : ''}>
            {/* Section Title */}
            <div className="px-3 mb-3 mt-2">
              <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                {section.title}
              </h3>
            </div>

            {/* Section Items */}
            <div className="space-y-0.5">
              {section.items.filter((item) => {
                if (item.emailOnly && !item.emailOnly.includes(user?.email)) return false
                if (typeof item.hiddenIf === 'function' && item.hiddenIf(user)) return false
                return true
              }).map((item) => {
                const resolvedBadge = item.dynamicBadge === 'activation' ? activationBadge : item.badge
                // Parse href to separate path and query for accurate matching
                const [itemPath, itemQuery] = item.href.split('?')
                const currentFullPath = currentPath + location.search
                let isActive
                if (itemQuery) {
                  // Item has query params (e.g. /video-analysis?tab=profile) — match both path + query
                  isActive = currentPath === itemPath && location.search.includes(itemQuery)
                } else {
                  // No query params — match path exactly, but NOT if another sibling with query matches
                  isActive = (currentPath === itemPath && !location.search) || currentPath.startsWith(itemPath + '/')
                }
                const isComingSoon = item.comingSoon || (item.name === 'Контент-план' && !isAdmin)

                if (isComingSoon) {
                  const description = item.name === 'Контент-план' && !isAdmin
                    ? 'Контент-план (скоро)'
                    : item.description

                  return (
                    <div
                      key={item.name}
                      title={description}
                      className="group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg text-slate-600 cursor-not-allowed opacity-50"
                    >
                      <div className="flex items-center">
                        <item.icon className="w-5 h-5 mr-3" />
                        <span className="flex-1">{item.name}</span>
                      </div>
                      <span className="text-[10px] text-slate-500 bg-white/10 px-2 py-0.5 rounded-full">
                        скоро
                      </span>
                    </div>
                  )
                }

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    title={item.description}
                    data-tour-id={item.tourId}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    <span className="flex-1">{item.name}</span>
                    {resolvedBadge && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${item.dynamicBadge === 'activation' || resolvedBadge === 'new' ? 'text-zinc-900 bg-brand-lime font-bold' : 'text-purple-300 bg-purple-500/20'}`}>
                        {resolvedBadge}
                      </span>
                    )}
                  </Link>
                )
              })}
            </div>
          </div>
        ))}

        {/* Integrations Section — видно только integrations admin email
            whitelist (на MVP-этапе пока не открыли для всех платных юзеров) */}
        {isIntegrationsAdmin(user) && (
          <div className="mt-8 mb-4">
            <div className="px-3 mb-3 mt-2">
              <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                ИНТЕГРАЦИИ
              </h3>
            </div>
            <div className="space-y-0.5">
              {integrationsNavigation.map((item) => {
                const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/')
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Admin Section */}
        {isAdmin && (
          <div className="mt-8 mb-4">
            <div className="px-3 mb-3 mt-2">
              <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">
                АДМИН
              </h3>
            </div>
            <div className="space-y-0.5">
              {adminNavigation.map((item) => {
                const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/')
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-slate-400 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <item.icon className="w-5 h-5 mr-3" />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        )}
      </nav>

      {/* Promo banner — hidden on /subscription and /onboarding routes only */}
      {promoBanner && !['/subscription', '/onboarding'].some(p => location.pathname.startsWith(p)) && (
        <PromoBanner
          banner={promoBanner}
          onCta={handlePromoCta}
          onDismiss={handlePromoDismiss}
        />
      )}

      {/* Profile Section */}
      <ProfileDropdown user={user} subscription={subscription} />
    </div>
  )
}

export default Layout